import type { Track } from '@/world/Track';
import { tuning } from '@/config/tuning';
import { Car } from '@/entities/Car';
import { Ball } from '@/entities/Ball';
import type { RacerIdentity } from '@/config/racers';
import { createDriverMemory, resetDriverMemory, type DriverMemory } from '@/ai/Driver';
import { createPowerupState, resetPowerupState, type PowerupState } from '@/physics/powerups';

/** Where a racer's car and ball start (in its lane). */
interface StartPose {
  cx: number;
  cz: number;
  cy: number;
  heading: number;
  bx: number;
  bz: number;
  by: number;
}

/**
 * SimRacer.ts — the PURE, three.js-free half of a racer: its Car + Ball physics
 * entities, identity, AI memory, the Mario-Kart item slot, and race bookkeeping
 * (finished / finish order / stun). This is the unit the authoritative SimCore
 * steps — it can run unchanged on a headless server (a Cloudflare Durable
 * Object) because it imports no rendering.
 *
 * Its visual twin (CarView + BallView meshes) lives on the CLIENT, in Race.ts,
 * keyed by `id` — the views read this state each frame; the sim never knows they
 * exist. (Was entities/Racer.ts before the M18 headless split.)
 */
export class SimRacer {
  readonly id: number;
  readonly name: string;
  readonly color: number;
  readonly isPlayer: boolean;
  /** Drives the level-scaled AI engine (M15) rather than the stock one. Keyed off
   *  identity, NOT "who has an input this tick", so a client doing full-world
   *  prediction (it supplies inputs for everyone) still gives bots their engine. */
  readonly isBot: boolean;
  readonly lane: number;
  /** Per-racer pace multiplier (M14 field skill: aces > 1, backmarkers < 1). */
  readonly paceScale: number;

  readonly car: Car;
  readonly ball: Ball;

  finished = false;
  finishOrder = -1;
  /** Race-clock time (seconds) when this racer's ball crossed the line. */
  finishTime: number | null = null;

  /** Per-racer AI state (used for the rivals; harmless for the player). */
  readonly driverMemory: DriverMemory;

  /** The Mario-Kart item slot (M12) — same engine for the player and the AI. */
  readonly powerups: PowerupState = createPowerupState();
  /** Bulldozer stun: seconds of lost control remaining after being rammed. */
  stunTimer = 0;
  /** Forced steer sign (±1) while stunned — the spin-out direction. */
  stunSteer = 0;
  /** Boost-pad surge (M47): seconds of engine boost left from a course pad. */
  padBoost = 0;
  /** LAUNCH MAGNET (M117c): seconds the shortcut launch ramp's temporary
   *  magnet keeps this racer's ball riding the nose — through the flight and
   *  the landing settle, so jump accuracy + speed decide entries, not ball
   *  luck. Humans only (granted on launch-ramp contact). Synced. */
  launchMagnet = 0;
  /** SCORPION KICK (M59): seconds the car is locked in place doing the flip
   *  after launching its ball skyward. While > 0 the car holds position (no
   *  control, intangible). Synced so every client's prediction freezes alike.
   *  M60: DECOUPLED from the ball flight — the car lands + drives (this hits 0)
   *  well before the ball finishes its 300-unit arc (scorpionBallTimer). */
  scorpionTimer = 0;
  /** SCORPION KICK ball-flight seconds left (M60): while > 0 this racer's ball
   *  is UNTOUCHABLE (skips every collision) — it sails its full arc, only
   *  gravity + guardrails acting, then lands and bounces normally. Synced. */
  scorpionBallTimer = 0;
  /** SCORPION KICKs this racer has RECEIVED this match (M60d) — capped at
   *  SCORPION_MAX. NOT a physics field: item rolls are authoritative on the
   *  server, so this is a hidden accumulator (like the item RNG), never synced. */
  scorpionCount = 0;
  /** SECRET SHORTCUT (M117): which channel this racer's CAR is currently
   *  driving (index into SimCore.shortcuts; −1 = the road). While set, the car
   *  is updated against the CHANNEL's Track (its floor + walls) and interacts
   *  only with entities in the same space. The player keeps FULL control —
   *  they drive the tunnel themselves. Synced. */
  carChannel = -1;
  /** Same for this racer's BALL — car and ball thread the doorway (and exit)
   *  independently, each judged by real aperture geometry. Synced. */
  ballChannel = -1;
  /** Derived standings bookkeeping (NOT synced — recomputed by SimCore each
   *  step and right after snapshot adoption): the channel's entry/exit course
   *  arcs + length + the ball's projected distance along it, for the
   *  ballDistance() lerp while the ball is off the road. */
  chFrom = 0;
  chTo = 0;
  chLen = 0;
  ballChDist = 0;
  /** The vehicle this racer drives (M51 — REAL in the sim, every mode): a
   *  human's pick brings its full stat profile; a bot keeps the tuned AI
   *  engine but its vehicle WEIGHT is real in car-car shoves. Defaults are
   *  assigned by SimCore.buildField; online, snapshots carry the truth.
   *  NOT cleared by reset() — a rematch keeps everyone's ride. */
  vehicleId = 'sports';

  constructor(
    identity: RacerIdentity,
    track: Track,
    lane: number,
    paceScale = 1,
    isBot = !identity.isPlayer,
  ) {
    this.id = identity.id;
    this.name = identity.name;
    this.color = identity.color;
    this.isPlayer = identity.isPlayer;
    // A bot drives the level-scaled AI engine; a HUMAN-occupied slot drives the
    // stock engine. Offline it's "every rival" (the default); online the room
    // passes the real roster so each connected player gets the stock engine.
    this.isBot = isBot;
    this.lane = lane;
    this.paceScale = paceScale;
    this.driverMemory = createDriverMemory(identity.id * 1000 + 7);

    const pose = this.startPose(track);
    this.car = new Car(pose.cx, pose.cz, pose.heading, pose.cy);
    this.ball = new Ball(pose.bx, pose.by, pose.bz);
  }

  private startPose(track: Track): StartPose {
    const s = track.start;
    const cx = s.x + s.rightX * this.lane;
    const cz = s.z + s.rightZ * this.lane;
    const a = track.sampleAt(tuning.racers.ballAhead); // ball sits a little ahead of the car
    const bx = a.x + a.rightX * this.lane;
    const bz = a.z + a.rightZ * this.lane;
    return {
      cx,
      cz,
      cy: track.heightAt(cx, cz),
      heading: s.dir,
      bx,
      bz,
      by: track.heightAt(bx, bz) + tuning.ball.radius,
    };
  }

  reset(track: Track): void {
    const pose = this.startPose(track);
    this.car.reset(pose.cx, pose.cz, pose.heading, pose.cy);
    this.ball.reset(pose.bx, pose.by, pose.bz);
    this.finished = false;
    this.finishOrder = -1;
    this.finishTime = null;
    resetDriverMemory(this.driverMemory);
    resetPowerupState(this.powerups);
    this.stunTimer = 0;
    this.stunSteer = 0;
    this.padBoost = 0;
    this.launchMagnet = 0;
    this.scorpionTimer = 0;
    this.scorpionBallTimer = 0;
    this.scorpionCount = 0;
    this.carChannel = -1;
    this.ballChannel = -1;
    this.chFrom = 0;
    this.chTo = 0;
    this.chLen = 0;
    this.ballChDist = 0;
  }

  /** Arc-length of this racer's ball along the track — the basis for standings. */
  ballDistance(track: Track): number {
    // Inside a SHORTCUT channel (M117) the ball is physically OFF the road
    // (under the block / through the hillside), so projecting it would read a
    // bogus nearest-segment arc. Standings map the ball's REAL progress along
    // the channel onto the entry→exit arc instead — a pure function of synced
    // state (ball position + channel index), so every client agrees.
    if (this.ballChannel >= 0 && this.chLen > 0) {
      const k = Math.min(1, Math.max(0, this.ballChDist / this.chLen));
      return this.chFrom + (this.chTo - this.chFrom) * k;
    }
    return track.project(this.ball.state.x, this.ball.state.z, this.ball.hint).distance;
  }
}
